import React from 'react';

export interface NotificationEntry {
  readonly notificationId: string;
  readonly message: string;
  readonly actorName: string;
  readonly read: boolean;
  readonly createdAt: string;
}

export interface NotificationPanelProps {
  readonly notifications: NotificationEntry[];
  readonly unreadCount: number;
  readonly onMarkRead: (notificationId: string) => void;
  readonly onMarkAllRead: () => void;
}

/**
 * Notification panel — displays notifications with unread badge (ARIA live region),
 * read/unread visual distinction, mark-read on click, mark-all-read button.
 */
export function NotificationPanel({
  notifications,
  unreadCount,
  onMarkRead,
  onMarkAllRead,
}: NotificationPanelProps): React.ReactElement {
  return (
    <section aria-label="Notifications" className="p-4 bg-white rounded shadow">
      {/* Header with badge */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-sm text-gray-800">Notifications</h3>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <span
              aria-live="polite"
              aria-label={`${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`}
              className="bg-danger-500 text-white text-xs rounded-full px-2 py-0.5 font-medium"
            >
              {unreadCount}
            </span>
          )}
          <button
            onClick={onMarkAllRead}
            aria-label="Mark all as read"
            className="text-xs text-primary-700 hover:underline"
          >
            Mark all read
          </button>
        </div>
      </div>

      {/* Notification list */}
      {notifications.length === 0 ? (
        <p className="text-sm text-gray-400">No notifications yet.</p>
      ) : (
        <ul className="space-y-2">
          {notifications.map((notif) => (
            <li
              key={notif.notificationId}
              data-read={notif.read}
              className={`p-2 rounded cursor-pointer ${notif.read ? 'bg-gray-50 text-gray-500' : 'bg-primary-50 text-gray-900'}`}
            >
              <button
                onClick={() => onMarkRead(notif.notificationId)}
                className="w-full text-left text-sm"
              >
                <span className="font-medium">{notif.actorName}</span>{' '}
                <span>{notif.message}</span>
                <time dateTime={notif.createdAt} className="block text-xs text-gray-400 mt-0.5">
                  {new Date(notif.createdAt).toLocaleString()}
                </time>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
